import type { Editor } from "@tiptap/core";
import type { EditorState } from "@tiptap/pm/state";
import { DOMParser as PMDOMParser } from "@tiptap/pm/model";
import type {
    EditorAdapter,
    OpenDocumentRef,
    ProposedEdit,
    EditResolution
} from "@codenexus/react";

interface ProposedNodeEdit {
    editId: string;
    docId: string;
    nodeId: string;
    new_source: string;
    rationale?: string;
}
import { isStructuralMarkdown, renderMarkdownToHtml } from "./markdown";
import { currentSections } from "./workProductContext";
import type { HeadlessSectionEditors } from "./HeadlessSectionEditors";

interface RegisteredDoc {
    editor: Editor;
    sectionId: string | number;
    title: string;
}

/**
 * One section in the workproduct the user has open. Multiple sections are
 * "available" but only one is mounted at a time (the active one); the others
 * exist in Redux state and can be navigated to via a host-side dispatch.
 */
export interface AvailableSection {
    id: string | number;
    title: string;
}

interface MarkdownStorage {
    markdown?: { getMarkdown: () => string };
}

function getMarkdown(editor: Editor): string {
    const storage = editor.storage as MarkdownStorage;
    // tiptap-markdown attaches a `markdown` storage. If it isn't present
    // (unlikely given TiptapEditor configures it) fall back to plain text so
    // search-and-replace can still operate.
    const raw = storage.markdown?.getMarkdown() ?? editor.getText();
    return normalizeOrderedListNumbers(raw);
}

/**
 * Replace explicit sequential numbers in ordered-list markdown (2., 3., 4. …)
 * with "1." so the agent never needs to renumber trailing items after an
 * insertion. The editor stores list position structurally (<ol>) and
 * auto-numbers in HTML; the numbers in markdown are display-only decoration.
 * The agent identifies items by text content (old_string), not by number, so
 * this is safe to normalize.
 */
function normalizeOrderedListNumbers(markdown: string): string {
    return markdown.replace(/^[2-9]\d*\. /gm, "1. ");
}

type RangeSearchResult =
    | { status: "ok"; from: number; to: number }
    | { status: "ambiguous" }
    | { status: "multi_block" }
    | { status: "not_found" };

/**
 * Search the editor for `query`, returning a PM range that lies inside a
 * single textblock — the only kind of range a PM mark can span.
 *
 * Algorithm:
 *   • Walk top-level textblocks.
 *   • In each, build a "plain text" string by concatenating the text of
 *     every descendant text node (this strips inline-mark structure so the
 *     same query matches whether or not the block has bold/italic/link
 *     runs).
 *   • For each occurrence found within a block, record the corresponding
 *     PM positions by mapping the in-block character offset back through
 *     the text runs we walked.
 *   • If exactly one occurrence is found across the whole doc, return it.
 *     Two-or-more is `ambiguous`. Zero is either `not_found` or, if the
 *     query appears in the markdown serialization across `\n\n` block
 *     joints, `multi_block` (signalling the caller to surface a useful
 *     error to the agent).
 */
function findSingleBlockRange(editor: Editor, query: string): RangeSearchResult {
    if (!query) return { status: "not_found" };

    const matches: Array<{ from: number; to: number }> = [];

    editor.state.doc.descendants((node, pos) => {
        if (!node.isTextblock) return true; // recurse into containers
        // Build the block's plain text + a run table so we can map flat
        // offsets back to PM positions across inline-mark boundaries.
        let blockText = "";
        const runs: Array<{ pmStart: number; blockStart: number; length: number }> = [];
        node.descendants((child, childPos) => {
            if (child.isText && child.text) {
                runs.push({
                    pmStart: pos + 1 + childPos, // +1 to enter the block
                    blockStart: blockText.length,
                    length: child.text.length
                });
                blockText += child.text;
                return false; // text nodes have no further children
            }
            return true;
        });

        let searchFrom = 0;
        while (true) {
            const idx = blockText.indexOf(query, searchFrom);
            if (idx === -1) break;
            const endIdx = idx + query.length;
            const startRun = runs.find(
                r => idx >= r.blockStart && idx < r.blockStart + r.length
            );
            const endRun = runs.find(
                r => endIdx > r.blockStart && endIdx <= r.blockStart + r.length
            );
            if (startRun && endRun) {
                matches.push({
                    from: startRun.pmStart + (idx - startRun.blockStart),
                    to: endRun.pmStart + (endIdx - endRun.blockStart)
                });
            }
            searchFrom = idx + query.length;
            if (matches.length > 1) return false; // early-out: ambiguous
        }
        return false; // textblocks aren't nested inside other textblocks
    });

    if (matches.length > 1) return { status: "ambiguous" };
    if (matches.length === 1) return { status: "ok", ...matches[0]! };

    // Zero single-block matches. Check if the query appears across blocks
    // via the markdown serialization, so the caller can report a more
    // useful error. We don't try to apply such an edit — PM marks can't
    // span block boundaries.
    const storage = (editor.storage as { markdown?: { getMarkdown: () => string } }).markdown;
    const md = storage?.getMarkdown() ?? "";
    if (md && md.includes(query) && query.includes("\n")) {
        return { status: "multi_block" };
    }
    return { status: "not_found" };
}

/**
 * Adapter that exposes the single currently-mounted TipTap editor to the
 * @codenexus/react host-document bridge. Each TiptapEditor instance registers
 * itself on mount via `register(...)` and unregisters on unmount. The active
 * tab in DocSectionContainer reinitializes the TiptapEditor on every section
 * switch (via `key={editorKey}`) so there is always at most one registered
 * editor at a time.
 *
 * Multi-doc support (listing every section in a workproduct simultaneously)
 * is a follow-up — bridge protocol already supports it; just requires the
 * frontend to mount multiple editors or hold their markdown snapshots.
 */
/**
 * Tracks which staging mechanism (inline mark vs block node) a given editId
 * was wired up with, so resolve() can dispatch to the right TipTap command.
 *
 * For 'replace' block-mode edits we also store the paired anchorEditId — the
 * mark editId on the original anchor block — although in practice it equals
 * the same editId because we use one for the whole staged edit. The map keys
 * are the editIds emitted by the @codenexus/react bridge.
 */
type StagingKind = "inline" | "block";

/**
 * Returns true if `pos` is nested inside a list item node.
 */
function isInsideListItem(state: EditorState, pos: number): boolean {
    const $pos = state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
        const name = $pos.node(d).type.name;
        if (name === "listItem" || name === "list_item") return true;
    }
    return false;
}

export class IntelligenicTipTapAdapter implements EditorAdapter {
    private current: RegisteredDoc | null = null;
    private availableSections: AvailableSection[] = [];
    private changeListeners = new Set<(refs: OpenDocumentRef[]) => void>();
    private resolveListeners = new Set<(r: EditResolution) => void>();
    private updateUnsub: (() => void) | null = null;
    private stagingKind = new Map<string, StagingKind>();
    private headless: HeadlessSectionEditors | null = null;
    private readOnly = false;

    /** UI parity guard; backend/collab enforcement remains authoritative. */
    setReadOnly(readOnly: boolean): void {
        this.readOnly = readOnly;
        if (readOnly) this.headless?.releaseAll();
    }

    /**
     * Wire in the headless-editor pool (Task 4) so agent reads/edits can
     * reach a section's collab peer even when it isn't the mounted editor.
     * Called once from HostDocumentBridgeProvider.
     */
    attachHeadless(h: HeadlessSectionEditors): void {
        this.headless = h;
    }

    /**
     * Register the full list of sections of the currently-open workproduct.
     * Called by DocSectionContainer when sections load or change. The agent
     * sees these via listOpen() — the active section has full read/edit
     * access, the rest are listed by name only so the agent can request a
     * switch via switch_open_document.
     */
    setSections(sections: AvailableSection[]): void {
        const a = this.availableSections.map(s => `${s.id}|${s.title}`).join("\n");
        const b = sections.map(s => `${s.id}|${s.title}`).join("\n");
        if (a === b) return;
        this.availableSections = sections.map(s => ({ id: s.id, title: s.title }));
        this.notifyChange();
    }

    /**
     * Get the available sections (e.g. for host code that needs to map a
     * docId from a tool call back to its section index for dispatch).
     */
    getSections(): AvailableSection[] {
        return [...this.availableSections];
    }

    /**
     * Returns true if the currently-mounted editor has any unresolved staged
     * agent edits (inline marks OR block-level wrappers). Used by the host
     * to decide whether to prompt the user before switching sections.
     */
    hasPendingEdits(): boolean {
        if (!this.current) return false;
        const editor = this.current.editor;
        let found = false;
        editor.state.doc.descendants(node => {
            if (found) return false;
            if (node.type.name === "agent_suggestion_block") {
                found = true;
                return false;
            }
            if (
                node.marks.some(
                    m =>
                        m.type.name === "agent_suggestion" ||
                        m.type.name === "agent_pending_replace"
                )
            ) {
                found = true;
                return false;
            }
            return true;
        });
        return found;
    }

    register(editor: Editor, sectionId: string | number, title: string): () => void {
        // If there is already a registered editor, unsub from its update
        // signal before swapping. The tab system normally unmounts the old
        // editor first, but defensive cleanup never hurts.
        this.updateUnsub?.();
        this.current = { editor, sectionId, title };

        const onUpdate = () => {
            this.notifyChange();
        };
        editor.on("update", onUpdate);
        this.updateUnsub = () => editor.off("update", onUpdate);

        this.notifyChange();
        return () => {
            if (this.current?.editor === editor) {
                this.updateUnsub?.();
                this.updateUnsub = null;
                this.current = null;
                this.notifyChange();
            }
        };
    }

    /**
     * Returns the currently-registered TipTap editor iff it belongs to
     * `sectionId` — used by callers (e.g. the AI-chat preview Accept path in
     * collab mode) that need to apply a change directly to the live editor
     * instance rather than through a value prop, so the write becomes a local
     * Yjs transaction instead of diverging from the shared ydoc. Returns
     * undefined if no editor is registered or the registered one is for a
     * different section (e.g. the user switched tabs mid-flight).
     */
    getEditorForSection(sectionId: string | number): Editor | undefined {
        if (!this.current) return undefined;
        return String(this.current.sectionId) === String(sectionId) ? this.current.editor : undefined;
    }

    private docId(): string | null {
        if (!this.current) return null;
        return `section-${this.current.sectionId}`;
    }

    /**
     * Resolve a docId to the editor the agent should operate on. The mounted
     * editor is preferred when it IS this doc (cheapest, already synced);
     * otherwise the headless peer for the section. All staging works
     * identically on either — both are collab peers on the same fragment.
     */
    private async resolveEditor(docId: string): Promise<
        | { status: "ok"; editor: Editor }
        | { status: "failed"; reason: "doc_not_open" | "doc_unavailable" }
    > {
        if (this.current && docId === this.docId()) {
            return { status: "ok", editor: this.current.editor };
        }
        if (!this.headless) return { status: "failed", reason: "doc_not_open" };
        return this.headless.getEditor(docId);
    }

    private notifyChange(): void {
        const refs = this.listOpen();
        this.changeListeners.forEach(l => l(refs));
    }

    listOpen(): OpenDocumentRef[] {
        const activeId = this.docId();
        // Prefer the live Redux section list (works even with no mounted
        // editor); fall back to whatever setSections() pushed for hosts that
        // still use that path.
        const fromStore = currentSections().map(s => ({
            id: `section-${s.sectionId}`,
            title: s.title,
            kind: "prose" as const,
            language: "markdown",
            active: false
        }));
        const refs: OpenDocumentRef[] = fromStore.length > 0
            ? fromStore
            : this.availableSections.map(s => ({
                  id: `section-${s.id}`,
                  title: s.title || "Untitled Section",
                  kind: "prose" as const,
                  language: "markdown",
                  active: false
              }));

        if (activeId && this.current) {
            const existing = refs.find(r => r.id === activeId);
            if (existing) {
                existing.active = true;
                existing.byteLength = getMarkdown(this.current.editor).length;
            } else {
                // Active section isn't in the available list yet (race during
                // load) — surface it anyway so the agent doesn't see "no doc".
                refs.unshift({
                    id: activeId,
                    title: this.current.title || "Untitled Section",
                    kind: "prose",
                    language: "markdown",
                    active: true,
                    byteLength: getMarkdown(this.current.editor).length
                });
            }
        }

        return refs;
    }

    async read(docId: string): Promise<{ content: string; ref: OpenDocumentRef }> {
        const resolved = await this.resolveEditor(docId);
        if (resolved.status === "failed") throw new Error(resolved.reason);
        const refs = this.listOpen();
        const ref = refs.find(r => r.id === docId) ?? refs[0]!;
        return { content: getMarkdown(resolved.editor), ref };
    }

    async applyEdit(
        edit: ProposedEdit
    ): Promise<{ status: "staged" } | { status: "failed"; reason: string }> {
        if (this.readOnly) return { status: "failed", reason: "access_denied" };
        const resolved = await this.resolveEditor(edit.docId);
        if (resolved.status === "failed") {
            return { status: "failed", reason: resolved.reason };
        }
        const editor = resolved.editor;

        // Search the editor's plain text content (not the markdown
        // serialization) for `old_string`. Two reasons:
        //
        //   1. Inline marks (bold, italic, links) split a paragraph's PM
        //      content into multiple text nodes — searching each node
        //      individually misses anchors that span across the split.
        //   2. The markdown serialization contains syntax markers (** _ `
        //      etc.) that don't appear in PM text content. The agent's
        //      anchor often comes verbatim from the markdown; matching it
        //      against PM text content lets it succeed even when inline
        //      markup differs.
        //
        // PM suggestion marks can only span a single textblock, so we
        // search per-textblock and reject anchors that would cross block
        // boundaries (returning `old_string_not_found` with a hint).
        let range = findSingleBlockRange(editor, edit.old_string);
        // Agents sometimes prefix list-item anchors with their rendered number
        // (e.g. "4. Send a short push notification…") even though the textblock
        // stores only the bare text. Strip the leading list marker and retry
        // once so the agent doesn't have to surface a failure and retry itself.
        if (range.status === "not_found") {
            const stripped = edit.old_string.replace(/^(\d+\.\s+|[-*+]\s+)/, "");
            if (stripped !== edit.old_string) {
                range = findSingleBlockRange(editor, stripped);
            }
        }
        if (range.status === "ambiguous") {
            return { status: "failed", reason: "old_string_ambiguous" };
        }
        if (range.status === "not_found") {
            return { status: "failed", reason: "old_string_not_found" };
        }
        if (range.status === "multi_block") {
            // The anchor exists in the doc but spans more than one block
            // (e.g. it contains `\n\n` joining two paragraphs). Suggestion
            // marks can't span blocks; report as not_found so the agent
            // retries with a single-block anchor.
            return { status: "failed", reason: "old_string_not_found" };
        }

        // Branch on whether new_string is plain inline text (use the
        // existing agent_suggestion mark) or multi-block markdown (parse it
        // and stage as an agent_suggestion_block sibling of the anchor).
        if (!isStructuralMarkdown(edit.new_string)) {
            return this.stageInline(editor, edit, range);
        }
        return this.stageStructural(editor, edit, range);
    }

    /**
     * Inline staging: replaces just the text range with the proposed text
     * via the existing agent_suggestion mark. Renders strike-through + green
     * replacement chip inline. Accept replaces the marked text with
     * new_string; reject removes the mark.
     */
    private stageInline(
        editor: Editor,
        edit: ProposedEdit,
        range: { from: number; to: number }
    ): { status: "staged" } {
        this.stagingKind.set(edit.editId, "inline");
        editor
            .chain()
            .setTextSelection({ from: range.from, to: range.to })
            .setMark("agent_suggestion", {
                editId: edit.editId,
                newString: edit.new_string,
                rationale: edit.rationale ?? ""
            })
            .run();
        return { status: "staged" };
    }

    /**
     * Structural staging: parses new_string as markdown, builds an
     * agent_suggestion_block wrapping the resulting blocks, inserts it
     * IMMEDIATELY AFTER the anchor's containing block. Also applies an
     * agent_pending_replace mark across the anchor text so the user sees the
     * "before → after" pairing in the editor.
     *
     * This covers add-paragraph, add-list, expand-section-with-summary,
     * convert-list-type, and any future operation where the agent emits
     * multi-block markdown (including content fetched from external tools
     * — the adapter doesn't care where new_string came from).
     *
     * Special case — list "insert after": when the anchor is a list item AND
     * the first line of new_string (stripped of its list marker) equals
     * old_string (stripped), the anchor text is unchanged and new_string is
     * really "anchor + new items to append". In that case we drop the
     * repeated anchor line from the suggestion block and skip the
     * pending-replace mark, so the user sees only the genuinely new items
     * rather than a confusing duplicate of the anchor followed by the rest.
     */
    private stageStructural(
        editor: Editor,
        edit: ProposedEdit,
        range: { from: number; to: number }
    ): { status: "staged" } | { status: "failed"; reason: string } {
        // Find the block containing the matched range — we need its end
        // position so we can insert the suggestion block right after it.
        const anchorBlockEnd = findContainingBlockEnd(editor.state, range.from);
        if (anchorBlockEnd === null) {
            return { status: "failed", reason: "old_string_not_found" };
        }

        // Detect "insert after" semantics for list items: new_string starts
        // with a repetition of old_string (possibly with a renumbered marker).
        const anchorInList = isInsideListItem(editor.state, range.from);
        const stripMarker = (s: string) => s.replace(/^(\d+\.\s+|[-*+]\s+)/, "").trim();
        const newLines = edit.new_string.split("\n");
        const firstLineStripped = stripMarker(newLines[0] ?? "");
        const oldStripped = stripMarker(edit.old_string);
        const isInsertAfter =
            anchorInList &&
            newLines.length > 1 &&
            firstLineStripped === oldStripped;

        const markdownForBlock = isInsertAfter
            ? newLines.slice(1).join("\n").trimStart()
            : edit.new_string;

        const mode = isInsertAfter ? "insert" : "replace";

        // Compute the correct starting number for the suggestion block's <ol>
        // so it displays "5." rather than "1." when inserting after item 4.
        let insertStartNumber = 1;
        if (isInsertAfter) {
            const $anchor = editor.state.doc.resolve(range.from);
            for (let d = $anchor.depth; d >= 1; d--) {
                const name = $anchor.node(d).type.name;
                if (name === "listItem" || name === "list_item") {
                    // 0-based index of this listItem in its parent list
                    const anchorIndex = $anchor.index(d - 1);
                    insertStartNumber = anchorIndex + 2; // 1-based position + 1 for "after"
                    break;
                }
            }
        }

        let html = renderMarkdownToHtml(markdownForBlock);
        if (isInsertAfter && insertStartNumber > 1) {
            html = html.replace(/^<ol>/i, `<ol start="${insertStartNumber}">`);
        }
        const wrapperHtml = buildSuggestionBlockHtml({
            editId: edit.editId,
            rationale: edit.rationale ?? "",
            anchorEditId: edit.editId,
            mode,
            innerHtml: html,
            // For insert-after list items, store the anchor text so acceptSuggestionBlock
            // can find the right list item and splice new items in at the correct position.
            insertAnchorText: isInsertAfter ? oldStripped : undefined
        });

        this.stagingKind.set(edit.editId, "block");

        if (!isInsertAfter) {
            // Phase 1: apply the pending-replace mark via TipTap's chain.
            editor
                .chain()
                .setTextSelection({ from: range.from, to: range.to })
                .setMark("agent_pending_replace", { editId: edit.editId })
                .run();
        }

        // Phase 2: insert the suggestion block via a direct PM dispatch — NOT
        // inside the chain above. TipTap batches chain steps and remaps
        // positions as it applies them; that remapping was dropping the block
        // back into the last list node. Using editor.view.dispatch with a fresh
        // transaction on editor.state (post-mark) avoids the remapping entirely.
        const container = document.createElement('div');
        container.innerHTML = wrapperHtml;
        const parsed = PMDOMParser.fromSchema(editor.state.schema).parse(container);
        editor.view.dispatch(editor.state.tr.insert(anchorBlockEnd, parsed.content));

        return { status: "staged" };
    }

    async applyNodeEdit(
        edit: ProposedNodeEdit
    ): Promise<{ status: 'staged' } | { status: 'failed'; reason: string }> {
        if (this.readOnly) return { status: 'failed', reason: 'access_denied' };
        const resolved = await this.resolveEditor(edit.docId);
        if (resolved.status === "failed") {
            return { status: 'failed', reason: resolved.reason };
        }
        const editor = resolved.editor;

        let nodePos: number | null = null;
        let nodeType: "mermaid" | "table" | null = null;
        editor.state.doc.descendants((node, pos) => {
            if (nodePos !== null) return false;
            if (
                (node.type.name === 'mermaid' || node.type.name === 'table') &&
                node.attrs.nodeId === edit.nodeId
            ) {
                nodePos = pos;
                nodeType = node.type.name as "mermaid" | "table";
            }
            return true;
        });

        if (nodePos === null || nodeType === null) {
            return { status: 'failed', reason: 'node_not_found' };
        }

        const targetNode = editor.state.doc.nodeAt(nodePos)!;
        const insertAfter = nodePos + targetNode.nodeSize;

        let innerHtml = "";
        if (nodeType === "mermaid") {
            const escapedSource = edit.new_source
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;');
            innerHtml = `<pre><code class="language-mermaid">${escapedSource}</code></pre>`;
        } else {
            const tableMarkdown = normaliseIncomingTableMarkdown(edit.new_source);
            innerHtml = renderMarkdownToHtml(tableMarkdown);
        }

        const wrapperHtml = buildSuggestionBlockHtml({
            editId: edit.editId,
            rationale: edit.rationale ?? '',
            anchorEditId: edit.editId,
            mode: 'replace',
            innerHtml,
        });

        this.stagingKind.set(edit.editId, 'block');

        editor.chain()
            .command(({ tr }) => {
                tr.setNodeAttribute(nodePos!, 'pendingReplaceEditId', edit.editId);
                if (nodeType === "mermaid") {
                    tr.setNodeAttribute(nodePos!, 'pendingReplaceCode', edit.new_source);
                } else {
                    tr.setNodeAttribute(nodePos!, 'pendingReplaceMarkdown', normaliseIncomingTableMarkdown(edit.new_source));
                }
                return true;
            })
            .insertContentAt(insertAfter, wrapperHtml)
            .run();

        return { status: 'staged' };
    }

    onChange(l: (refs: OpenDocumentRef[]) => void): () => void {
        this.changeListeners.add(l);
        return () => {
            this.changeListeners.delete(l);
        };
    }

    onEditResolved(l: (r: EditResolution) => void): () => void {
        this.resolveListeners.add(l);
        return () => {
            this.resolveListeners.delete(l);
        };
    }

    /**
     * Invoked by TiptapEditor's click delegation when the user clicks a
     * staged suggestion span or block. Dispatches to the right TipTap command
     * based on how the edit was staged.
     */
    resolve(editId: string, status: "accepted" | "rejected"): void {
        if (this.readOnly) return;
        const editor = this.current?.editor;
        if (!editor) return;

        // Check if this is a pending mermaid node replacement
        let mermaidPos: number | null = null;
        let pendingCode = '';
        let tablePos: number | null = null;
        let pendingTableMarkdown = '';
        editor.state.doc.descendants((node, pos) => {
            if (mermaidPos !== null || tablePos !== null) return false;
            if (node.type.name === 'mermaid' && node.attrs.pendingReplaceEditId === editId) {
                mermaidPos = pos;
                pendingCode = node.attrs.pendingReplaceCode ?? '';
                return false;
            }
            if (node.type.name === 'table' && node.attrs.pendingReplaceEditId === editId) {
                tablePos = pos;
                pendingTableMarkdown = node.attrs.pendingReplaceMarkdown ?? '';
            }
            return true;
        });

        if (mermaidPos !== null) {
            editor.chain()
                .focus()
                .command(({ tr }) => {
                    const node = editor.state.doc.nodeAt(mermaidPos!);
                    if (!node) return true;
                    tr.setNodeMarkup(mermaidPos!, undefined, {
                        ...node.attrs,
                        ...(status === 'accepted' ? { code: pendingCode } : {}),
                        pendingReplaceEditId: null,
                        pendingReplaceCode: null,
                    });
                    return true;
                })
                .run();
            // The original node was already updated in-place by setNodeMarkup above.
            // Always reject (remove) the preview suggestion block — accepting it would
            // unwrap it into a second mermaid node.
            editor.commands.rejectSuggestionBlock(editId);
        } else if (tablePos !== null) {
            if (status === "accepted") {
                const replacement = buildTableNodeFromMarkdown(editor, pendingTableMarkdown, {
                    nodeId: editor.state.doc.nodeAt(tablePos)?.attrs.nodeId ?? null
                });
                if (replacement) {
                    const tableNode = editor.state.doc.nodeAt(tablePos);
                    if (tableNode) {
                        editor.view.dispatch(
                            editor.state.tr.replaceWith(tablePos, tablePos + tableNode.nodeSize, replacement)
                        );
                    }
                } else {
                    // If parsing fails, fall back to keeping the original table unchanged.
                    const tableNode = editor.state.doc.nodeAt(tablePos);
                    if (tableNode) {
                        editor.chain()
                            .focus()
                            .command(({ tr }) => {
                                tr.setNodeMarkup(tablePos!, undefined, {
                                    ...tableNode.attrs,
                                    pendingReplaceEditId: null,
                                    pendingReplaceMarkdown: null
                                });
                                return true;
                            })
                            .run();
                    }
                }
            } else {
                const tableNode = editor.state.doc.nodeAt(tablePos);
                if (tableNode) {
                    editor.chain()
                        .focus()
                        .command(({ tr }) => {
                            tr.setNodeMarkup(tablePos!, undefined, {
                                ...tableNode.attrs,
                                pendingReplaceEditId: null,
                                pendingReplaceMarkdown: null
                            });
                            return true;
                        })
                        .run();
                }
            }
            // The original table node is updated/reverted in-place. Remove the
            // preview suggestion block regardless of resolution outcome.
            editor.commands.rejectSuggestionBlock(editId);
        } else {
            const kind = this.stagingKind.get(editId) ?? this.detectKind(editor, editId);
            if (kind === "block") {
                if (status === "accepted") editor.commands.acceptSuggestionBlock(editId);
                else editor.commands.rejectSuggestionBlock(editId);
            } else {
                if (status === "accepted") editor.commands.acceptSuggestion(editId);
                else editor.commands.rejectSuggestion(editId);
            }
        }

        this.stagingKind.delete(editId);
        this.resolveListeners.forEach(l => l({ editId, status }));
    }

    /**
     * Fallback when the stagingKind map doesn't have an entry (e.g. an edit
     * was staged before the adapter was constructed, or the map was lost in
     * a hot reload). Walks the doc looking for either a block node or a mark
     * carrying this editId.
     */
    private detectKind(editor: Editor, editId: string): StagingKind {
        let kind: StagingKind = "inline";
        editor.state.doc.descendants(node => {
            if (kind === "block") return false;
            if (node.type.name === "agent_suggestion_block" && node.attrs.editId === editId) {
                kind = "block";
                return false;
            }
            return true;
        });
        return kind;
    }
}

/**
 * Find the end position of the textblock that contains `pos`. Used to insert
 * a suggestion block immediately after the anchor's containing paragraph /
 * heading / list-item. Returns null if no textblock ancestor exists (shouldn't
 * happen for valid ranges).
 */
function findContainingBlockEnd(state: EditorState, pos: number): number | null {
    const $pos = state.doc.resolve(pos);
    for (let depth = $pos.depth; depth >= 0; depth -= 1) {
        if (!$pos.node(depth).isTextblock) continue;
        // Insertion after a list-item's paragraph would land mid-list, which
        // is not where the user wants the suggestion. Walk up while parents
        // are list-shaped so we end after the enclosing list as a whole.
        let useDepth = depth;
        for (let d = depth - 1; d >= 1; d -= 1) {
            const name = $pos.node(d).type.name;
            if (name === "listItem" || name === "list_item" || name === "bulletList" || name === "orderedList") {
                useDepth = d;
            } else {
                break;
            }
        }
        // `after(d)` returns the absolute position immediately after the
        // closing of the node at depth d. That's where we insert the
        // suggestion block as the next sibling. Use depth=0 (doc) only as a
        // last resort by clamping to content.size.
        if (useDepth === 0) return state.doc.content.size;
        return $pos.after(useDepth);
    }
    return null;
}

/**
 * Build the raw HTML for an agent_suggestion_block. The HTML attributes match
 * what AgentSuggestionBlock.parseHTML expects, so when this string is fed to
 * editor.commands.insertContentAt the editor parses it into the custom node
 * with the right attrs.
 *
 * innerHtml is the markdown-rendered HTML of new_string. It can include any
 * block-level tags (headings, lists, paragraphs, blockquotes, code blocks);
 * tiptap's parser will turn them into the corresponding PM nodes inside the
 * wrapper, since AgentSuggestionBlock's content spec is `block+`.
 */
function buildSuggestionBlockHtml(args: {
    editId: string;
    rationale: string;
    anchorEditId: string;
    mode: "insert" | "replace";
    innerHtml: string;
    insertAnchorText?: string;
}): string {
    const { editId, rationale, anchorEditId, mode, innerHtml, insertAnchorText } = args;
    const escapeAttr = (s: string): string =>
        s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const anchorAttr = insertAnchorText
        ? ` data-insert-anchor-text="${escapeAttr(insertAnchorText)}"`
        : "";
    return `<div data-agent-suggestion-block="true" data-edit-id="${escapeAttr(editId)}" data-anchor-edit-id="${escapeAttr(anchorEditId)}" data-mode="${escapeAttr(mode)}" data-rationale="${escapeAttr(rationale)}"${anchorAttr}>${innerHtml}</div>`;
}

function normaliseIncomingTableMarkdown(source: string): string {
    return source.replace(/^\s*<!--\s*table:[^>]+-->\s*\n?/i, "").trim();
}

function buildTableNodeFromMarkdown(
    editor: Editor,
    markdown: string,
    opts: { nodeId: string | null }
): any | null {
    const tableMarkdown = normaliseIncomingTableMarkdown(markdown);
    if (!tableMarkdown) return null;

    const html = renderMarkdownToHtml(tableMarkdown);
    const container = document.createElement("div");
    container.innerHTML = html;
    const parsed = PMDOMParser.fromSchema(editor.state.schema).parse(container);

    let tableNode: any | null = null;
    parsed.descendants((node: any) => {
        if (tableNode) return false;
        if (node.type.name === "table") {
            tableNode = node;
            return false;
        }
        return true;
    });
    if (!tableNode) return null;

    const tableType = editor.state.schema.nodes.table;
    if (!tableType) return null;
    return tableType.create(
        {
            ...tableNode.attrs,
            nodeId: opts.nodeId,
            pendingReplaceEditId: null,
            pendingReplaceMarkdown: null
        },
        tableNode.content
    );
}
